import React, { useState, ReactElement, useCallback, useRef } from 'react';

import {
  Container,
  InputWrapper,
  InputField,
  Prefix,
  Postfix,
  Loader,
  ArrowIcon,
} from '../styles';
import { SelectOption } from '../types';
import { Options, Option } from './styles';
import { Spinner } from '../../Spinner';
import { useOutsideClickListener } from '../../../../hooks/useOutsideClickListener';
import { Checkbox } from '../../Checkbox';

export const MultiSelect = ({
  className,
  name,
  prefix,
  postfix,
  options,
  selected,
  placeholder,
  multipleSelectedMessage,
  onSelect,
  onBlur,
  disabled,
  dataCy,
  isLoading = false,
}: MultiSelectProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const menuRef = useRef(null);
  const onOutsideClick = useCallback(() => {
    setIsOpen(false);
  }, []);
  useOutsideClickListener(menuRef, onOutsideClick);

  const onOptionSelected = (value: string, isSelected: boolean) => {
    if (isSelected) {
      const selectedOption = options.find((option) => option.value === value);
      if (selectedOption) onSelect([...selected, selectedOption]);
    } else {
      onSelect(selected.filter((option) => option.value !== value));
    }
  };

  return (
    <Container
      className={className}
      data-cy={dataCy}
      $hasError={false}
      ref={menuRef}
      disabled={disabled}
    >
      <InputWrapper $active={isOpen || isLoading}>
        {prefix && <Prefix>{prefix}</Prefix>}
        <InputField
          name={name}
          type="text"
          autoComplete="nope"
          value={formatSelectedLabel(selected, multipleSelectedMessage)}
          readOnly
          placeholder={disabled ? '' : placeholder}
          onClick={() => setIsOpen(!isOpen)}
          onBlur={(e: React.FocusEvent) => onBlur && onBlur(e)}
        />
        {postfix || (
          <Postfix>
            <ArrowIcon title="Open" />
          </Postfix>
        )}
      </InputWrapper>
      <Options disabled={disabled} hidden={!isOpen}>
        {isLoading ? (
          <Loader>
            <Spinner scale={0.6} />
          </Loader>
        ) : (
          <>
            {options.map((option) => (
              <Option
                disabled={disabled}
                key={option.value}
                onClick={() => onSelect([option])}
              >
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    name={
                      option.value ? `${name || ''}_${option.value}` : 'empty'
                    }
                    value={option.value || ''}
                    label={option.label}
                    disabled={disabled}
                    selected={selected?.some(
                      (item) => item.value === option.value,
                    )}
                    onSelect={onOptionSelected}
                  />
                </div>
              </Option>
            ))}
          </>
        )}
      </Options>
    </Container>
  );
};

function formatSelectedLabel(
  selected?: SelectOption[],
  multipleSelectedMessage?: string,
) {
  if (!selected?.length) return '';
  if (selected.length === 1) return selected[0].label;
  return `${selected.length} ${multipleSelectedMessage || 'items selected'}`;
}

export interface MultiSelectProps {
  className?: string;
  name?: string;
  prefix?: ReactElement;
  postfix?: ReactElement;
  options: SelectOption[];
  selected: SelectOption[];
  placeholder?: string;
  // Shown when there are multiple values selected in the format '<number of selected items> <multipleSelectedMessage>'
  multipleSelectedMessage?: string;
  isLoading?: boolean;
  disabled?: boolean;
  dataCy?: string;
  onSelect: (values: SelectOption[]) => void;
  onBlur?: (e: React.FocusEvent) => void;
}
